import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  RefreshCw, FileText, Tag, Activity, TrendingUp, Cpu,
} from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import JobStatusBadge from '../components/JobStatusBadge';
import { api } from '../services/api';
import type { AdminJob } from '../types';
import { fmtAge, fmtDuration } from '../utils/format';

const TYPE_META: Record<string, { label: string; color: string }> = {
  'sync':                 { label: 'Meeting Sync',       color: '#7c3aed' },
  'backfill-sync':        { label: 'Meeting Backfill',   color: '#a78bfa' },
  'reprocess-sync':       { label: 'Meeting Reprocess',  color: '#f59e0b' },
  'ai-extraction':        { label: 'Task Extraction',    color: '#60a5fa' },
  'ai-themes':            { label: 'Theme Assignment',   color: '#c084fc' },
  'ai-speaker-sentiment': { label: 'Speaker Sentiment',  color: '#34d399' },
  'ai-sentiment':         { label: 'Sentiment Analysis', color: '#f59e0b' },
};

function typeMeta(type: string) {
  return TYPE_META[type] ?? { label: type, color: '#94a3b8' };
}

const POLL_INTERVAL_ACTIVE = 5000;
const POLL_INTERVAL_IDLE = 15000;

export default function JobsPage() {
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchJobs = async (initial = false) => {
    try {
      const r = await api.get('/admin/jobs?limit=300');
      setJobs(r.data.jobs);
    } catch {
      // keep previous data
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(true);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const hasRunning = jobs.some(j => j.status === 'running');
    timerRef.current = setTimeout(() => fetchJobs(), hasRunning ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_IDLE);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [jobs]);

  const orgIds = useMemo(() => Array.from(new Set(jobs.map(j => j.orgId))).sort(), [jobs]);

  const filtered = useMemo(() => {
    let out = jobs;
    if (statusFilter) out = out.filter(j => j.status === statusFilter);
    if (typeFilter) out = out.filter(j => j.type === typeFilter);
    if (orgFilter) out = out.filter(j => j.orgId === orgFilter);
    return out;
  }, [jobs, statusFilter, typeFilter, orgFilter]);

  const running = jobs.filter(j => j.status === 'running').length;
  const failed = jobs.filter(j => j.status === 'failed').length;
  const completed = jobs.filter(j => j.status === 'completed').length;

  const columns: Column<AdminJob>[] = [
    {
      key: 'type',
      label: 'Type',
      render: row => {
        const m = typeMeta(row.type);
        return (
          <span style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{m.label}</span>
        );
      },
    },
    {
      key: 'orgId',
      label: 'Org',
      render: row => (
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
          {row.orgId.slice(0, 12)}…
        </span>
      ),
    },
    {
      key: 'label',
      label: 'Detail',
      render: row => {
        const isFailed = row.status === 'failed';
        return (
          <div>
            <div style={{ fontWeight: 500, marginBottom: 2 }}>{row.label}</div>
            {row.detail && (
              <div
                title={row.detail}
                style={{
                  fontSize: 11,
                  color: isFailed ? 'var(--danger)' : 'var(--text-muted)',
                  maxWidth: 340,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: isFailed ? 'normal' : 'nowrap',
                  wordBreak: isFailed ? 'break-word' : undefined,
                  lineClamp: isFailed ? 3 : undefined,
                  WebkitLineClamp: isFailed ? 3 : undefined,
                  display: isFailed ? '-webkit-box' : undefined,
                  WebkitBoxOrient: isFailed ? 'vertical' : undefined,
                }}
              >
                {row.detail}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: row => <JobStatusBadge status={row.status} />,
    },
    {
      key: 'startedAt',
      label: 'Started',
      sortable: true,
      render: row => fmtAge(row.startedAt),
    },
    {
      key: 'durationMs',
      label: 'Duration',
      render: row => fmtDuration(row.durationMs) || '—',
    },
    {
      key: 'result',
      label: 'Result',
      render: row => {
        const r = row.result;
        if (!r) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
        const parts = [];
        if (r.processed != null) parts.push(`${r.processed} processed`);
        if (r.tasksCreated != null) parts.push(`${r.tasksCreated} created`);
        if (r.errors) parts.push(`${r.errors} errors`);
        return <span style={{ fontSize: 11, color: r.errors ? 'var(--danger)' : 'var(--text-muted)' }}>{parts.join(' · ') || '—'}</span>;
      },
    },
  ];

  const selectStyle: React.CSSProperties = {
    padding: '7px 10px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    color: 'var(--text)',
    fontSize: 12,
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Jobs</h1>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {running > 0 && <span style={{ color: 'var(--warning)' }}>{running} running · </span>}
          {completed} completed · {failed > 0 && <span style={{ color: 'var(--danger)' }}>{failed} failed · </span>}
          {jobs.length} total (24h)
        </span>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="">All statuses</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle}>
          <option value="">All types</option>
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} style={selectStyle}>
          <option value="">All orgs</option>
          {orgIds.map(id => (
            <option key={id} value={id}>{id.slice(0, 16)}…</option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: 'var(--text-dim)', alignSelf: 'center' }}>
          {filtered.length} job{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <DataTable<AdminJob>
        columns={columns}
        data={filtered}
        loading={loading}
        emptyMessage="No jobs"
        rowKey={row => row.jobId}
      />
    </div>
  );
}
